import { useMemo, type CSSProperties, type ReactNode } from 'react'
import type {
  Blackout,
  CalendarLeave,
  PublicHoliday,
} from '@lms/shared-types'
import { HIDDEN_LEAVE_TYPE, leaveTypeStyle } from '../../lib/leave-type-palette'
import { teamColorStyle, type TeamColorStyle } from '../../lib/team-color-palette'
import { useViewport } from '../../lib/use-viewport'
import { LeaveBar } from '../primitives/LeaveBar'
import { MonthGrid } from '../primitives/MonthGrid'
import type { MonthGridDayMeta } from '../primitives/MonthGrid'
import { WeekBarOverlay } from '../primitives/WeekBarOverlay'
import type { WeekBarSegment } from '../primitives/WeekBarOverlay'
import { Tooltip } from '../primitives/Tooltip'

export interface PersonOut {
  id: string
  name: string
}

export interface TeamCalendarGridProps {
  /** Sun-first month cells (use buildMonthCells from the page). */
  cells: ReadonlyArray<{ date: string | null }>
  leaves: ReadonlyArray<CalendarLeave>
  blackouts: ReadonlyArray<Blackout>
  publicHolidays: ReadonlyArray<PublicHoliday>
  today: string
  /** Highlight leaves from the viewer's own team in a dedicated colour. */
  ownTeamId: string | null
  /**
   * ISO-date → people absent that day. Drives the "N out: Name1, Name2"
   * tooltip on each day cell. Build with buildPeopleOut() in the parent.
   */
  peopleOut?: ReadonlyMap<string, ReadonlyArray<PersonOut>>
  /**
   * When supplied, REPLACES the default holiday-only dayMeta build.
   * The parent has already merged holidays + signals into this map.
   */
  dayMeta?: ReadonlyMap<string, MonthGridDayMeta>
  /**
   * Optional click handler — fires when a non-blank cell is clicked.
   * Use the second arg as the anchor element for popover positioning.
   */
  onCellClick?: (date: string, anchor: HTMLElement) => void
  /**
   * Increments each time the "Today" button is clicked. Re-mounting the
   * today day-number element (via key) re-runs the lms-today-pulse animation.
   */
  todayPulseId?: number
  /**
   * Desktop cell min-height in px. Default 154 — sized for a 4-row bar stack
   * (the full Team Calendar). Compact surfaces (e.g. the personal home
   * calendar, which shows only the viewer's own leaves) can pass a smaller
   * value alongside a lower `maxBarRows`.
   */
  cellMinHeight?: number
  /** Max stacked bar rows before bars overflow into a "+N more" chip. Default 4. */
  maxBarRows?: number
  /** Week column start — `monday` (default) or `sunday`. Must match `cells`. */
  weekStartsOn?: 'monday' | 'sunday'
  /** Resolve a team's colour. Defaults to the hash-based palette. */
  teamColor?: (teamId: string) => TeamColorStyle
}

/** Payload attached to each bar segment so renderBar gets full context. */
interface LeaveBarData {
  leave: CalendarLeave
  ownTeamId: string | null
}

const VISIBLE_ROWS = 3

/**
 * A blackout band shares the cell's vertical space with the leaf bars, so it
 * counts against the row cap: each blackout in the week steals one leaf row
 * (e.g. a 3-row cap becomes 2 leaf bars + a "+N more" when a blackout is
 * present).
 */
function effectiveLeafRows(maxRows: number, blackoutBands: number): number {
  return Math.max(maxRows - blackoutBands, 1)
}

/**
 * Max leave-type dots rendered in a single mobile cell before the rest fold
 * into a "+N" chip. Mobile columns are ~40–50px wide; two dots + an overflow
 * chip is the legible ceiling. Mirrors the desktop "+N more" affordance.
 */
const MOBILE_MAX_LEAVE_DOTS = 2

/**
 * Replicates WeekBarOverlay's greedy first-fit packing to compute, for each
 * column in the week, how many bars on that day were pushed beyond the
 * `maxRows` cap. Returns a map keyed by ISO date (one entry per cell
 * with overflow > 0).
 */
function computePerDayOverflow(
  weekCells: ReadonlyArray<{ date: string | null }>,
  weekBars: ReadonlyArray<WeekBarSegment<LeaveBarData>>,
  maxRows: number,
): Map<string, number> {
  // Greedy first-fit packing — same logic as WeekBarOverlay.
  const sorted = [...weekBars].sort((a, b) => a.startCol - b.startCol)
  const rows: WeekBarSegment<LeaveBarData>[][] = []
  for (const seg of sorted) {
    let placed = false
    for (const row of rows) {
      const last = row[row.length - 1]!
      if (last.endCol < seg.startCol) {
        row.push(seg)
        placed = true
        break
      }
    }
    if (!placed) rows.push([seg])
  }
  // Tally per-column overflow from rows beyond the visible cap.
  const overflowByCol = new Map<number, number>()
  for (let r = maxRows; r < rows.length; r++) {
    for (const seg of rows[r]!) {
      for (let col = seg.startCol; col <= seg.endCol; col++) {
        overflowByCol.set(col, (overflowByCol.get(col) ?? 0) + 1)
      }
    }
  }
  // Map column index back to the cell's ISO date.
  const out = new Map<string, number>()
  weekCells.forEach((cell, col) => {
    if (cell.date === null) return
    const count = overflowByCol.get(col)
    if (count !== undefined && count > 0) out.set(cell.date, count)
  })
  return out
}

/**
 * Wireframe-matched team calendar (`new_reference/surface-employee.jsx::CalendarGrid`).
 * Renders the month as a stack of week rows; each week row layers continuous
 * leave bars over the day cells, breaking only at week boundaries (so a
 * Mon→Fri leave is one bar, not five). Bars stack greedily up to 3 rows
 * (desktop) and overflow into a "+N more" badge.
 *
 * Layout and bar packing are delegated to MonthGrid + WeekBarOverlay primitives.
 */
export function TeamCalendarGrid({
  cells,
  leaves,
  blackouts,
  publicHolidays,
  today,
  ownTeamId,
  peopleOut,
  dayMeta: propDayMeta,
  onCellClick,
  todayPulseId,
  cellMinHeight = 118,
  maxBarRows = VISIBLE_ROWS,
  weekStartsOn = 'monday',
  teamColor = teamColorStyle,
}: TeamCalendarGridProps): JSX.Element {
  const { isMobile } = useViewport()

  // When the parent supplies dayMeta it has already merged holidays + signals;
  // otherwise fall back to the default holiday-only build.
  const dayMeta: ReadonlyMap<string, MonthGridDayMeta> =
    propDayMeta ??
    new Map<string, MonthGridDayMeta>(
      publicHolidays.map((h) => [h.date, { publicHoliday: h.name }]),
    )

  const blackoutDates = useMemo(() => {
    const set = new Set<string>()
    for (const bl of blackouts) {
      const start = new Date(`${bl.startDate}T00:00:00Z`)
      const end = new Date(`${bl.endDate}T00:00:00Z`)
      const cursor = new Date(start)
      while (cursor <= end) {
        set.add(cursor.toISOString().slice(0, 10))
        cursor.setUTCDate(cursor.getUTCDate() + 1)
      }
    }
    return set
  }, [blackouts])

  // Merge blackout flags into the day metadata so MonthGrid fills those cells
  // with the hatch background (behind the day number). The red naming pill is
  // layered separately in WeekOverlay.
  const dayMetaWithBlackout = useMemo(() => {
    if (blackoutDates.size === 0) return dayMeta
    const merged = new Map<string, MonthGridDayMeta>(dayMeta)
    for (const iso of blackoutDates) {
      merged.set(iso, { ...(merged.get(iso) ?? {}), blackout: true })
    }
    return merged as ReadonlyMap<string, MonthGridDayMeta>
  }, [dayMeta, blackoutDates])

  // Per-day leave index — ISO date → every leave covering that date. Drives
  // the mobile leave-type dot stack. Both callers always pass `leaves`, so the
  // personal home calendar (which passes no `peopleOut`) still renders a
  // marker for the viewer's own leaves on mobile. The team calendar passes
  // `peopleOut` and keeps its count-chip treatment; this map is only read on
  // mobile when `peopleOut` is absent.
  const leavesByDate = useMemo(() => {
    const out = new Map<string, CalendarLeave[]>()
    for (const leave of leaves) {
      const start = new Date(`${leave.startDate}T00:00:00Z`)
      const end = new Date(`${leave.endDate}T00:00:00Z`)
      const cursor = new Date(start)
      while (cursor <= end) {
        const iso = cursor.toISOString().slice(0, 10)
        const list = out.get(iso) ?? []
        list.push(leave)
        out.set(iso, list)
        cursor.setUTCDate(cursor.getUTCDate() + 1)
      }
    }
    return out
  }, [leaves])

  // Pre-compute per-day overflow counts so each day cell can render its own
  // "+N more" chip rather than the misleading week-row-level chip. Skipped on
  // mobile since bars are replaced by a count chip there.
  const overflowByDate = useMemo(() => {
    const result = new Map<string, number>()
    if (isMobile) return result
    for (let i = 0; i < cells.length; i += 7) {
      const weekCells = cells.slice(i, i + 7)
      const inMonth = weekCells.filter((c) => c.date !== null) as Array<{ date: string }>
      if (inMonth.length === 0) continue
      const weekMin = inMonth[0]!.date
      const weekMax = inMonth[inMonth.length - 1]!.date
      const weekBars: WeekBarSegment<LeaveBarData>[] = []
      for (const leave of leaves) {
        const s = leave.startDate > weekMin ? leave.startDate : weekMin
        const e = leave.endDate < weekMax ? leave.endDate : weekMax
        if (s > e) continue
        const startCol = weekCells.findIndex((c) => c.date === s)
        const endCol = weekCells.findIndex((c) => c.date === e)
        if (startCol < 0 || endCol < 0) continue
        weekBars.push({
          id: `${leave.id}-${weekMin}`,
          startCol,
          endCol,
          data: { leave, ownTeamId },
        })
      }
      // A blackout band in this week steals leaf rows from the cap.
      let weekBlackouts = 0
      for (const bl of blackouts) {
        if (bl.startDate <= weekMax && bl.endDate >= weekMin) weekBlackouts += 1
      }
      const weekOverflow = computePerDayOverflow(
        weekCells,
        weekBars,
        effectiveLeafRows(maxBarRows, weekBlackouts),
      )
      for (const [date, count] of weekOverflow) {
        result.set(date, count)
      }
    }
    return result
  }, [cells, leaves, blackouts, ownTeamId, isMobile, maxBarRows])

  // Mobile rendering: replace the gantt-style bar overlay with a per-cell
  // absent-count chip. Bars don't fit at ~50px column widths and the popover
  // still shows full details on tap.
  if (isMobile) {
    return (
      <MonthGrid
        cells={cells}
        today={today}
        dayMeta={dayMetaWithBlackout}
        data-testid="team-calendar-grid"
        weekStartsOn={weekStartsOn}
        cellMinHeight={64}
        {...(onCellClick !== undefined ? { onCellClick } : {})}
        renderCell={({ date, day, isToday, meta }) => {
          const absent = date !== null ? peopleOut?.get(date) : undefined
          const count = absent?.length ?? 0
          const isBlackout = date !== null && blackoutDates.has(date)
          const isHoliday = meta?.publicHoliday !== undefined
          // Both calendars show colored leave-type dots on mobile — bars don't
          // fit at ~50px columns, and a bare absent-count hides the leave type.
          // peopleOut still drives the hover tooltip / aria label (who's out by
          // name); the visible marker is the dots, with details on tap.
          const dayLeaves = date !== null ? leavesByDate.get(date) : undefined

          const dayNumber = (
            <span
              data-testid="calendar-cell"
              data-date={date ?? undefined}
              data-people-out={
                absent && absent.length > 0
                  ? `${absent.length} out: ${absent.map((p) => p.name).join(', ')}`
                  : dayLeaves && dayLeaves.length > 0
                    ? dayLeaves.map((l) => leaveTypeStyle(l.type).label).join(', ')
                    : undefined
              }
              key={isToday ? `today-${todayPulseId ?? 0}` : undefined}
              className={
                isToday && todayPulseId !== undefined && todayPulseId > 0
                  ? 'lms-today-pulse'
                  : undefined
              }
              style={{
                fontFamily: 'var(--font-mono, inherit)',
                fontSize: 12,
                fontWeight: isToday || isHoliday ? 700 : 500,
                color: isToday
                  ? 'var(--border)'
                  : isHoliday
                    ? 'var(--danger)'
                    : 'var(--text)',
                background: isToday ? 'var(--steel-blue)' : 'transparent',
                borderRadius: '50%',
                width: 22,
                height: 22,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                textAlign: 'center',
              }}
            >
              {day}
            </span>
          )

          const tooltipParts: string[] = []
          if (count > 0) {
            tooltipParts.push(
              `${count} out: ${absent!.map((p) => p.name).join(', ')}`,
            )
          } else if (dayLeaves && dayLeaves.length > 0) {
            tooltipParts.push(
              dayLeaves.map((l) => leaveTypeStyle(l.type).label).join(', '),
            )
          }
          if (isBlackout) tooltipParts.push('Blackout')
          const tooltipContent =
            tooltipParts.length > 0 ? tooltipParts.join('\n') : null

          const indicators = (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                minWidth: 0,
                maxWidth: '100%',
              }}
            >
              {dayLeaves && dayLeaves.length > 0 ? (
                <MobileLeaveDots leaves={dayLeaves} date={date} teamColor={teamColor} />
              ) : null}
              {isBlackout ? (
                <span
                  aria-label="Blackout"
                  data-testid="blackout-marker"
                  data-date={date ?? undefined}
                  style={{
                    fontSize: 11,
                    color: 'var(--blackout-ink)',
                    fontWeight: 700,
                  }}
                >
                  ⊘
                </span>
              ) : null}
            </span>
          )

          // Stack the day number over a centered dot row — a horizontal row
          // overflows the ~50px column and collides with the next cell. Same
          // layout on both the tooltip and no-tooltip paths.
          const stackStyle: CSSProperties = {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            minWidth: 0,
            width: '100%',
            overflow: 'hidden',
          }
          if (tooltipContent !== null) {
            return (
              <Tooltip content={tooltipContent} placement="top">
                <span style={stackStyle}>
                  {dayNumber}
                  {indicators}
                </span>
              </Tooltip>
            )
          }
          return (
            <span style={stackStyle}>
              {dayNumber}
              {indicators}
            </span>
          )
        }}
      />
    )
  }

  return (
    <MonthGrid
      cells={cells}
      today={today}
      dayMeta={dayMetaWithBlackout}
      data-testid="team-calendar-grid"
      weekStartsOn={weekStartsOn}
      // Default cell height accommodates the maxRows=3 bar stack + the "+N more"
      // overflow chip + day-number header (top:28) + bottom padding. Each bar
      // row is 20px + 4px gap → 3 rows bottom out at ~96px; plus header/chip
      // padding ≈ 118. Compact surfaces override via cellMinHeight + maxBarRows.
      cellMinHeight={cellMinHeight}
      {...(onCellClick !== undefined ? { onCellClick } : {})}
      renderCell={({ date, day, isToday, meta }) => {
        const isHoliday = meta?.publicHoliday !== undefined
        const absent = date !== null ? peopleOut?.get(date) : undefined
        const absentTitle =
          absent && absent.length > 0
            ? `${absent.length} out: ${absent.map((p) => p.name).join(', ')}`
            : undefined

        const overflow = date !== null ? overflowByDate.get(date) : undefined

        const dayNumber = (
          <span
            data-testid="calendar-cell"
            data-date={date ?? undefined}
            data-people-out={absentTitle ?? undefined}
            // Re-mount on each Today click so the CSS keyframe re-runs.
            key={isToday ? `today-${todayPulseId ?? 0}` : undefined}
            className={
              isToday && todayPulseId !== undefined && todayPulseId > 0
                ? 'lms-today-pulse'
                : undefined
            }
            style={{
              fontFamily: 'var(--font-mono, inherit)',
              fontSize: 12,
              fontWeight: isToday || isHoliday ? 700 : 500,
              color: isToday
                ? 'var(--surface)'
                : isHoliday
                  ? 'var(--danger)'
                  : 'var(--text)',
              background: isToday ? 'var(--steel-blue)' : 'transparent',
              borderRadius: '50%',
              width: 24,
              height: 24,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              textAlign: 'center',
            }}
          >
            {day}
          </span>
        )

        // Compose tooltip content — people-out list when present.
        let tooltipContent: ReactNode = null
        if (absentTitle !== undefined) {
          tooltipContent = absentTitle
        }

        // Per-cell overflow chip — anchored to the bottom-left of the cell
        // (position: absolute, so it doesn't disturb the flex header row).
        // pointerEvents: 'none' so clicks pass through to the cell handler.
        const overflowChip =
          overflow !== undefined && overflow > 0 ? (
            <span
              data-testid="more-leaves"
              data-date={date ?? undefined}
              style={{
                position: 'absolute',
                left: 8,
                bottom: 6,
                fontFamily: 'var(--font-mono, inherit)',
                fontSize: 10,
                fontWeight: 600,
                color: 'var(--text-3)',
                pointerEvents: 'none',
                zIndex: 2,
              }}
            >
              +{overflow} more
            </span>
          ) : null

        if (tooltipContent !== null) {
          return (
            <>
              <Tooltip content={tooltipContent} placement="top">
                {dayNumber}
              </Tooltip>
              {overflowChip}
            </>
          )
        }
        return (
          <>
            {dayNumber}
            {overflowChip}
          </>
        )
      }}
      renderWeekOverlay={({ weekCells }) => (
        <WeekOverlay
          weekCells={weekCells}
          leaves={leaves}
          blackouts={blackouts}
          ownTeamId={ownTeamId}
          maxRows={maxBarRows}
          teamColor={teamColor}
        />
      )}
    />
  )
}

// ---------------------------------------------------------------------------
// MobileLeaveDots — colored leave-type dot stack for mobile cells without a
// people-out count (the personal home calendar, which shows the viewer's own
// leaves). Each dot is tinted with the leave-type swatch so the marker reads
// the same hue as the desktop leave bar. Caps at MOBILE_MAX_LEAVE_DOTS and
// folds the remainder into a "+N" chip — the mobile analogue of the desktop
// "+N more" affordance.
// ---------------------------------------------------------------------------

interface MobileLeaveDotsProps {
  leaves: ReadonlyArray<CalendarLeave>
  date: string | null
  teamColor: (teamId: string) => TeamColorStyle
}

function MobileLeaveDots({
  leaves,
  date,
  teamColor,
}: Readonly<MobileLeaveDotsProps>): JSX.Element {
  const visible = leaves.slice(0, MOBILE_MAX_LEAVE_DOTS)
  const overflow = leaves.length - visible.length
  return (
    <span
      data-testid="mobile-leave-dots"
      data-date={date ?? undefined}
      data-leave-count={leaves.length}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        minWidth: 0,
      }}
    >
      {visible.map((leave, idx) => {
        const style = teamColor(leave.teamId)
        // Status encoding: approved = solid dot, anything still pending
        // (manager/director/needs-review/MC) = hollow ring in the same hue.
        const isApproved = leave.status === 'approved'
        return (
          <span
            key={`${leave.id}-${idx}`}
            data-testid="mobile-leave-dot"
            data-leave-type={leave.type}
            data-leave-id={leave.id}
            data-leave-status={leave.status}
            aria-hidden="true"
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              flexShrink: 0,
              background: isApproved ? style.swatch : 'transparent',
              border: `${isApproved ? 1 : 1.5}px solid ${style.swatch}`,
            }}
          />
        )
      })}
      {overflow > 0 ? (
        <span
          data-testid="mobile-leave-overflow"
          style={{
            fontFamily: 'var(--font-mono, inherit)',
            fontSize: 9,
            fontWeight: 700,
            color: 'var(--text-2)',
            lineHeight: 1,
          }}
        >
          +{overflow}
        </span>
      ) : null}
    </span>
  )
}

// ---------------------------------------------------------------------------
// WeekOverlay — builds segments + bands for one week row and delegates to
// WeekBarOverlay for rendering.
// ---------------------------------------------------------------------------

interface WeekOverlayProps {
  weekCells: ReadonlyArray<{ date: string | null }>
  leaves: ReadonlyArray<CalendarLeave>
  blackouts: ReadonlyArray<Blackout>
  ownTeamId: string | null
  maxRows: number
  teamColor: (teamId: string) => TeamColorStyle
}

function WeekOverlay({
  weekCells,
  leaves,
  blackouts,
  ownTeamId,
  maxRows,
  teamColor,
}: WeekOverlayProps): ReactNode {
  const inMonth = weekCells.filter((c) => c.date !== null) as Array<{ date: string }>
  if (inMonth.length === 0) return null

  const weekMin = inMonth[0]!.date
  const weekMax = inMonth[inMonth.length - 1]!.date

  function colOf(iso: string): number {
    return weekCells.findIndex((c) => c.date === iso)
  }

  // Build leave segments clipped to this week.
  const bars: WeekBarSegment<LeaveBarData>[] = []
  for (const leave of leaves) {
    const s = leave.startDate > weekMin ? leave.startDate : weekMin
    const e = leave.endDate < weekMax ? leave.endDate : weekMax
    if (s > e) continue
    const startCol = colOf(s)
    const endCol = colOf(e)
    if (startCol < 0 || endCol < 0) continue
    bars.push({
      id: `${leave.id}-${weekMin}`,
      startCol,
      endCol,
      data: {
        leave,
        ownTeamId,
      },
    })
  }

  // Build blackout bands for this week.
  const bands: Array<{
    id: string
    startCol: number
    endCol: number
    render: ReactNode
  }> = []
  for (const bl of blackouts) {
    const s = bl.startDate > weekMin ? bl.startDate : weekMin
    const e = bl.endDate < weekMax ? bl.endDate : weekMax
    if (s > e) continue
    const startCol = colOf(s)
    const endCol = colOf(e)
    if (startCol < 0 || endCol < 0) continue
    bands.push({
      id: `${bl.id}-${weekMin}`,
      startCol,
      endCol,
      render: (
        <Tooltip key={`blackout-${bl.id}`} content={bl.name} placement="top">
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '0 9px',
              background: 'var(--blackout-fill)',
              border: '1px solid var(--blackout-line)',
              borderRadius: 999,
              fontSize: 9.5,
              color: 'var(--blackout-ink)',
              fontWeight: 700,
              fontFamily: 'var(--font-mono, inherit)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              pointerEvents: 'auto',
            }}
          >
            <span
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}
            >
              BLACKOUT: {bl.name}
            </span>
          </div>
        </Tooltip>
      ),
    })
  }

  if (bars.length === 0 && bands.length === 0) return null

  return (
    <div
      style={{
        position: 'absolute',
        top: 28,
        left: 0,
        right: 0,
        bottom: 8,
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      <WeekBarOverlay<LeaveBarData>
        bars={bars}
        bands={bands}
        maxRows={effectiveLeafRows(maxRows, bands.length)}
        barHeight={20}
        rowGap={4}
        barGap={4}
        suppressOverflowRow={true}
        renderBar={({ data, startsHere, endsHere }) => {
          const { leave } = data
          // Privacy-masked leaves (type hidden) show the name only; when the
          // viewer can see the type, show "<Type> Leave" (e.g. "Annual Leave").
          const typeLabel =
            leave.type === HIDDEN_LEAVE_TYPE
              ? null
              : `${leaveTypeStyle(leave.type).label} Leave`
          // No own-team ring: team is already encoded by the bar colour, so the
          // extra inset ring just read as an inconsistent thick border.
          return (
            <LeaveBar
              leave={leave}
              startsHere={startsHere}
              endsHere={endsHere}
              variant="comfortable"
              density="low"
              colorStyle={teamColor(leave.teamId)}
              hideIcon
              nameFormat="name"
              typeLabel={typeLabel}
            />
          )
        }}
      />
    </div>
  )
}
